import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  Channel,
  channelApiClient,
  ChannelForm,
  ChannelShowResponse,
  ChannelUpdateErrorResponse,
} from '@src/api/shared/community/channelApiClient';
import { channelInviteRequestApiClient } from '@src/api/shared/community/channelInviteRequestApiClient';
import CreateEditChannelModal from '@src/components/CreateEditChannelModal.tsx';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { useChatEditing } from '@src/context/ChatEditingContext';
import { useIsModal } from '@src/context/IsModalContext';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import ChatBox, { ChatBoxHandle } from '@src/features/shared/community/components/ChatBox';
import { useToast } from '@src/features/toast/context/toastContext';
import { useChatMessages } from '@src/hooks/useChatMessages';
import { useDynamicHeight } from '@src/hooks/useDynamicHeightOptions';
import { useIsMobile } from '@src/hooks/useIsMobile';
import useTooltip from '@src/hooks/useTooltip';
import { useDynamicHeaderHeight } from '@src/hooks/useVirtualKeyboard';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL, ShowProps } from '@src/routes/company';
import { SHARED_URL } from '@src/routes/shared';
import { loadChannels, resetChannelCounts } from '@src/store/channelSlice.ts';
import { AppDispatch, RootState } from '@src/store/store.ts';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ChannelAccessDeniedLoggedIn from './components/ChannelAccessDeniedLoggedIn';
import ChannelAccessDeniedNotLoggedIn from './components/ChannelAccessDeniedNotLoggedIn';
import CommunityChannelHeader from './components/CommunityChannelHeader';
import { NAVIGATION_TARGET, NavigationTarget } from './constants/navigationTarget';
import PinnedChat from './PinnedChat';

const CHANNEL_REVALIDATION_INTERVAL = 60000;

function CommunityChatForm({ setFeature }: ShowProps) {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const [openSettingMenu, setOpenSettingMenu] = useState<boolean>(false);
  const [openChannelSettingModal, setOpenChannelSettingModal] = useState<boolean>(false);
  const [errors, setErrors] = useState<ChannelUpdateErrorResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const menuRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<ChatBoxHandle>(null);
  const tenantAuth = useTenantAuth();
  const companyAuth = useCompanyAuth();
  const navigate = useNavigate();
  const { editingChatId, openEditingLeaveModal } = useChatEditing();
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const isLoggedIn = !!(tenantAuth.profile?.isLogin || companyAuth.profile?.isLogin);
  const isTenantAdmin = !!tenantAuth.profile?.user?.isAdmin;
  const { showTooltip, displayTooltip } = useTooltip();
  const [mentionClickedChatId, setMentionClickedChatId] = useState<number | null>(null);
  const isFileDownloading = useSelector((state: RootState) => state.fileDownload.isFileDownloading);
  const { isAnyModalOpen } = useIsModal();

  const chatId = useMemo(() => {
    const chatIdFromUrl = searchParams.get('chatId')
      ? Number(searchParams.get('chatId'))
      : undefined;
    return chatIdFromUrl;
  }, [searchParams]);

  const {
    chats: chatGroups,
    pinnedChat,
    setPinnedChat,
    loadMoreChats,
    loadMoreNewerChats,
    hasMoreChats,
    hasMoreNewerChats,
    isDataReady: isChatDataReady,
    hideUnreadLines,
    firstUnreadChatId,
    mutate: mutateChats,
  } = useChatMessages(code, chatId);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!firstUnreadChatId) {
      return;
    }
    dispatch(loadChannels());
  }, [dispatch, firstUnreadChatId]);

  const handleHideUnreadLines = useCallback(() => {
    hideUnreadLines();
  }, [hideUnreadLines]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      handleHideUnreadLines();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleHideUnreadLines]);

  const { headerRef, footerRef, remeasure, mobileViewportHeight } = useDynamicHeight({
    additionalOffset: 0,
    useDynamicViewport: true,
  });

  const isMobile = useIsMobile(1024);
  const dynamicHeaderHeight = useDynamicHeaderHeight();

  const mobileStyles = useMemo(() => {
    if (!isMobile) {
      return {
        height: 'calc(100dvh - 61px)',
        maxHeight: undefined,
      };
    }

    return {
      height: `calc(${mobileViewportHeight}px - ${dynamicHeaderHeight}px)`,
      maxHeight: `calc(${mobileViewportHeight}px - ${dynamicHeaderHeight}px)`,
    };
  }, [isMobile, mobileViewportHeight, dynamicHeaderHeight]);

  const fetchChannelData = async (channelCode: string): Promise<ChannelShowResponse> => {
    const res = await channelApiClient.show(
      channelCode,
      undefined,
      getChannelAbortController(channelCode).signal,
    );
    let data: ChannelShowResponse = { channel: {} as Channel };

    handleApiResponse(res, (responseData) => {
      data = responseData;
    });

    return data;
  };

  const swrKey = code && !isAnyModalOpen ? `channel-${code}` : null;

  const [isChannelDataReady, setIsChannelDataReady] = useState(false);
  const lastChannelData = useRef<ChannelShowResponse | undefined>(undefined);

  // チャンネルごとにAbortControllerを管理
  const channelAbortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const getChannelAbortController = (channelCode: string): AbortController => {
    if (!channelAbortControllersRef.current.has(channelCode)) {
      channelAbortControllersRef.current.set(channelCode, new AbortController());
    }
    return channelAbortControllersRef.current.get(channelCode)!;
  };

  const {
    data: channelData,
    mutate: mutateChannel,
    isLoading: isChannelLoading,
  } = useSWR<ChannelShowResponse>(
    swrKey,
    () => {
      const result = fetchChannelData(code!);
      return result;
    },
    {
      refreshInterval: CHANNEL_REVALIDATION_INTERVAL,
      revalidateOnFocus: !isAnyModalOpen,
      onSuccess: (data) => {
        setLoading(false);
        setPinnedChat(data.channel.pinned_chat || undefined);
        setIsChannelDataReady(true);
        lastChannelData.current = data;
        setFeature(data.channel.name);
      },
    },
  );

  useEffect(() => {
    if (!isAnyModalOpen && channelData) {
      lastChannelData.current = channelData;
    }
  }, [isAnyModalOpen, channelData]);

  useEffect(() => {
    if (chatBoxRef.current) {
      const chatContainer = document.getElementById('chat-container');
      if (chatContainer) {
        chatContainer.scrollTop = 0;
      }
    }
  }, [code]);

  const handleInviteRequest = async () => {
    if (!code || inviteLoading || inviteSent) {
      return;
    }

    setInviteLoading(true);

    const res = await channelInviteRequestApiClient.sendInviteRequest(code);
    handleApiResponse(res, () => {
      toast('送信しました。');
      setInviteSent(true);
    });

    setInviteLoading(false);
  };

  const channel =
    (isAnyModalOpen ? lastChannelData.current?.channel : channelData?.channel) || null;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (editingChatId !== null) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editingChatId]);

  useEffect(() => {
    if (!code) {
      const redirectFirstChannel = async () => {
        if (isFileDownloading) {
          return;
        }
        const res = await channelApiClient.index(
          { with_unread: true },
          getChannelAbortController('channel-list').signal,
        );
        handleApiResponse(res, (data) => {
          if (data.length > 0) {
            const firstChannel = data[0];
            let url = generatePath(SHARED_URL.COMMUNITY_CHANNEL, { code: firstChannel.code });

            if (firstChannel.first_unread_chat_id) {
              url += `?chatId=${firstChannel.first_unread_chat_id}`;
              dispatch(resetChannelCounts(firstChannel.code));
            }

            navigate(url);
          }
        });
      };
      redirectFirstChannel();
      return;
    }
    setLoading(true);
  }, [code, navigate]);

  const fetchMoreChats = async () => {
    loadMoreChats();
  };

  const fetchMoreNewerChats = async () => {
    await loadMoreNewerChats();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenSettingMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    remeasure();
  }, [remeasure, pinnedChat]);

  const [isPageRefreshed, setIsPageRefreshed] = useState(false);

  useEffect(() => {
    const navigationEntries = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    const isRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';

    if (isRefresh) {
      setIsPageRefreshed(true);
    }
  }, []);

  useEffect(() => {
    if (code) {
      setIsChannelDataReady(false);
      setIsPageRefreshed(false);

      setTimeout(() => {
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) {
          chatContainer.scrollTop = 0;
        }
      }, 50);
    }
  }, [code]);

  const prevCodeRef = useRef<string | undefined>(undefined);

  // チャンネル変更時のAbortController管理
  useEffect(() => {
    const prevCode = prevCodeRef.current;

    // 初回レンダリング時は単に記録するだけ
    if (prevCode === undefined && code) {
      prevCodeRef.current = code;
      return;
    }

    if (prevCode === code) {
      return;
    }

    // 前のチャンネルのリクエストをキャンセル（存在する場合のみ）
    if (prevCode) {
      const prevAbortController = channelAbortControllersRef.current.get(prevCode);
      if (prevAbortController) {
        prevAbortController.abort();
        channelAbortControllersRef.current.delete(prevCode);
      }
    }

    // 新しいチャンネル用のAbortControllerは必要時に自動作成される
    prevCodeRef.current = code;
  }, [code]);

  const scrollInitTimeoutRef = useRef<NodeJS.Timeout>();
  const hasInitializedScroll = useRef(false);

  const channelScrollData = useMemo(() => {
    if (!channel) {
      return null;
    }

    return {
      code: channel.code,
      hasUnread: channel.has_unread,
      firstUnreadChatId: channel.first_unread_chat_id,
      unreadCount: channel.unread_count,
    };
  }, [channel?.code, channel?.has_unread, channel?.first_unread_chat_id, channel?.unread_count]);

  useEffect(() => {
    if (
      !chatBoxRef.current ||
      !channelScrollData ||
      !isChannelDataReady ||
      !isChatDataReady ||
      loading ||
      isChannelLoading ||
      chatGroups.length === 0 ||
      hasInitializedScroll.current
    ) {
      return;
    }

    if (chatId && channelScrollData.firstUnreadChatId !== chatId) {
      hasInitializedScroll.current = true;
      return;
    }

    if (scrollInitTimeoutRef.current) {
      clearTimeout(scrollInitTimeoutRef.current);
    }

    scrollInitTimeoutRef.current = setTimeout(() => {
      if (!chatBoxRef.current) {
        return;
      }

      hasInitializedScroll.current = true;

      if (isPageRefreshed) {
        chatBoxRef.current.scrollToUnreadOrBottom(null);
      } else if (
        channelScrollData.hasUnread &&
        channelScrollData.firstUnreadChatId &&
        (channelScrollData.unreadCount ?? 0) > 0
      ) {
        chatBoxRef.current.scrollToUnreadOrBottom(channelScrollData.firstUnreadChatId);
      } else {
        chatBoxRef.current.scrollToUnreadOrBottom(null);
      }
    }, 100);

    return () => {
      if (scrollInitTimeoutRef.current) {
        clearTimeout(scrollInitTimeoutRef.current);
      }
    };
  }, [
    channelScrollData,
    isChannelDataReady,
    isChatDataReady,
    loading,
    isChannelLoading,
    chatGroups.length,
    isPageRefreshed,
    chatId,
  ]);

  useEffect(() => {
    hasInitializedScroll.current = false;
    if (scrollInitTimeoutRef.current) {
      clearTimeout(scrollInitTimeoutRef.current);
    }
  }, [code]);

  useEffect(() => {
    return () => {
      if (scrollInitTimeoutRef.current) {
        clearTimeout(scrollInitTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setInviteLoading(false);
    setInviteSent(false);
  }, [code]);

  useEffect(() => {
    // チャンネルの情報が取れた段階でloadingをfalseに切り替えて表示できるものから表示
    if (channelData) {
      setLoading(false);
    }
  }, [channelData]);

  const handleFooterHeightChange = useCallback(() => {
    remeasure();
  }, [remeasure]);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, to: string) => {
      e.preventDefault();
      if (editingChatId !== null) {
        openEditingLeaveModal(to);
        return;
      }

      navigate(to);
    },
    [editingChatId, openEditingLeaveModal, navigate],
  );

  if (loading || isChannelLoading) {
    return null;
  }

  if (channel?.is_access_denied) {
    if (isLoggedIn) {
      return (
        <ChannelAccessDeniedLoggedIn
          channel={channel}
          inviteLoading={inviteLoading}
          inviteSent={inviteSent}
          onInviteRequest={handleInviteRequest}
          toggleMobileSidebar={toggleMobileSidebar}
        />
      );
    }

    const navigationTargetToUrl: Record<NavigationTarget, string> = {
      [NAVIGATION_TARGET.REGISTRATION]: COMPANY_URL.REGISTRATION,
      [NAVIGATION_TARGET.LOGIN]: COMPANY_URL.LOGIN,
    };

    return (
      <ChannelAccessDeniedNotLoggedIn
        channel={channel}
        toggleMobileSidebar={toggleMobileSidebar}
        navigate={(to) => {
          if (to in navigationTargetToUrl) {
            navigate(navigationTargetToUrl[to as NavigationTarget]);
          }
        }}
      />
    );
  }

  if (!channel) {
    return null;
  }

  const handleSettingMenuClick = () => {
    setOpenSettingMenu(true);
  };

  const handleChannelSettingOpenButtonClick = () => {
    setOpenSettingMenu(false);
    setOpenChannelSettingModal(true);
  };

  const handleChannelSettingModalClose = () => {
    setErrors(null);
    setOpenChannelSettingModal(false);
  };

  const onSave = async (formData: ChannelForm) => {
    if (!code) {
      return;
    }
    setErrors(null);
    const res = await channelApiClient.update(code, { channel: formData });
    handleApiResponse(
      res,
      async () => {
        await mutateChannel();
        setOpenChannelSettingModal(false);

        dispatch(loadChannels());
        toast(TOAST_MESSAGE.UPDATE);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  const onPinClick = async (chat_id: number | undefined) => {
    if (!code) {
      return;
    }
    const res = await channelApiClient.updatePinnedChat(code, { chat_id });
    handleApiResponse(res, () => {
      mutateChannel();
      mutateChats();
    });
  };

  const handlePinnedChatClick = () => {
    if (
      pinnedChat?.id &&
      chatBoxRef.current &&
      chatGroups.some((chatGroup) => chatGroup.chats.some((chat) => chat.id === pinnedChat?.id))
    ) {
      if (pinnedChat.id === channelScrollData?.firstUnreadChatId) {
        //表示しているデータに固定表示メッセージが存在しているならスクロールのみ行う
        chatBoxRef.current.scrollToUnreadOrBottom(pinnedChat.id);
      } else {
        chatBoxRef.current.scrollToChatById(pinnedChat.id);
      }
      return;
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('chatId', pinnedChat?.id?.toString() || '');
    navigate(currentUrl.pathname + currentUrl.search);
  };

  const onSend = () => {
    mutateChats();
  };

  return (
    <main
      className='flex flex-col lg:h-[calc(100vh-121px)]'
      style={mobileStyles}
    >
      <div
        ref={headerRef}
        className='sticky top-0 z-30 flex-[0_0_auto] bg-white lg:relative lg:z-auto'
      >
        <CommunityChannelHeader
          channel={channel}
          openSettingMenu={openSettingMenu}
          menuRef={menuRef}
          isTenantAdmin={isTenantAdmin}
          isLoggedIn={isLoggedIn}
          code={code}
          toggleMobileSidebar={toggleMobileSidebar}
          handleSettingMenuClick={handleSettingMenuClick}
          handleChannelSettingOpenButtonClick={handleChannelSettingOpenButtonClick}
          handleLinkClick={handleLinkClick}
          navigate={navigate}
        />
        {pinnedChat && (
          <PinnedChat
            pinnedChat={pinnedChat}
            channel={channel}
            isLoggedIn={isLoggedIn}
            chatBoxRef={chatBoxRef}
            onPinClick={onPinClick}
            onPinnedChatClick={handlePinnedChatClick}
            displayTooltip={displayTooltip}
            showTooltip={showTooltip}
            mentionClickedChatId={mentionClickedChatId}
            setMentionClickedChatId={setMentionClickedChatId}
          />
        )}
      </div>
      <ChatBox
        key={channel?.code}
        ref={chatBoxRef}
        channelCode={channel?.code || ''}
        channelName={channel?.name || ''}
        channelVisibility={channel?.visibility || undefined}
        canPost={channel?.can_post ?? false}
        allowFileAttachment={channel?.allow_file_attachment === 1}
        allowAnonymousPost={channel?.allow_anonymous_post === 1}
        isLoggedIn={isLoggedIn}
        chatGroups={chatGroups}
        fetchMoreChats={fetchMoreChats}
        fetchMoreNewerChats={fetchMoreNewerChats}
        hasMoreChats={hasMoreChats}
        hasMoreNewerChats={hasMoreNewerChats}
        pinnedChat={pinnedChat ?? undefined}
        onPinClick={onPinClick}
        onSend={onSend}
        mutateChats={mutateChats}
        footerRef={footerRef}
        onFooterHeightChange={handleFooterHeightChange}
        firstUnreadChatId={firstUnreadChatId}
        initialChatId={chatId}
      />
      {channel && (
        <CreateEditChannelModal
          isOpen={openChannelSettingModal}
          channel={channel}
          onSave={onSave}
          onClose={handleChannelSettingModalClose}
          errors={errors || undefined}
        />
      )}
    </main>
  );
}

export default CommunityChatForm;
